/**
 * <staffel-tiers> — volume discount ("staffelkorting") tier cards on the product page.
 *
 * Rendered by snippets/staffel-block.liquid, which also pre-renders every price
 * through the `money-ex-vat` snippet and hands them over as formatted strings. This
 * script therefore never formats money and never recalculates VAT; doing so would be
 * a second implementation of `money-ex-vat` drifting away from the first.
 *
 * There is one source of truth for the quantity: the form's own
 * `input[name="quantity"]`. This component reads and writes that field and derives
 * the card selection from it, never the other way round.
 */
(function () {
  if (customElements.get('staffel-tiers')) return;

  class StaffelTiers extends HTMLElement {
    connectedCallback() {
      this._section = this.closest('.shopify-section') || document;
      this._grid = this.querySelector('[data-staffel-grid]');
      this._cards = Array.from(this.querySelectorAll('[data-staffel-card]'));

      const dataEl = this.querySelector('[data-staffel-data]');
      try {
        this._data = JSON.parse(dataEl ? dataEl.textContent : '{}');
      } catch (error) {
        // A broken payload must not take the buy row down with it. The
        // server-rendered price stays on screen and the cards simply do nothing.
        console.warn('[staffel] kon de staffeldata niet lezen', error);
        return;
      }
      if (!this._data || !this._data.variants) return;

      this._variantId = this.dataset.variantId || '';
      this._bindControls();

      this._cards.forEach((card) => {
        card.addEventListener('click', () => this._pick(Number(card.dataset.quantity)));
      });
      this._grid?.addEventListener('keydown', (event) => this._onGridKeydown(event));

      this._onQuantityUpdate = (event) => {
        // Cart line updates share this event; they are not ours.
        if (event.detail && event.detail.cartLine) return;
        this._render();
      };
      document.addEventListener('quantity-selector:update', this._onQuantityUpdate);

      this._onInputChange = () => this._render();
      this._quantityInput?.addEventListener('change', this._onInputChange);

      // A variant change swaps `.main-product__price-row` wholesale (see
      // MainProductSync) and can replace the form controls, so re-bind and re-render
      // in the next frame, the same way the sticky bar does.
      this._onVariantUpdate = () => {
        requestAnimationFrame(() => {
          this._bindControls();
          const variantInput = this._section.querySelector('input[name="id"]');
          if (variantInput && variantInput.value) {
            this._variantId = variantInput.value;
            this.dataset.variantId = variantInput.value;
          }
          this._syncCardPrices();
          this._render();
        });
      };
      this._section.addEventListener('variant:update', this._onVariantUpdate);
      document.addEventListener('variant:update', this._onVariantUpdate);

      this._render();
    }

    disconnectedCallback() {
      document.removeEventListener('quantity-selector:update', this._onQuantityUpdate);
      document.removeEventListener('variant:update', this._onVariantUpdate);
      this._section?.removeEventListener('variant:update', this._onVariantUpdate);
      this._quantityInput?.removeEventListener('change', this._onInputChange);
    }

    /** Re-resolve the DOM this component writes into. Safe to call repeatedly. */
    _bindControls() {
      const previousInput = this._quantityInput;
      this._quantityInput = this._section.querySelector(
        'quantity-selector-component input[name="quantity"]'
      );
      if (previousInput && previousInput !== this._quantityInput && this._onInputChange) {
        previousInput.removeEventListener('change', this._onInputChange);
        this._quantityInput?.addEventListener('change', this._onInputChange);
      }
      this._priceEl = this._section.querySelector('[data-staffel-price]');
      this._compareEl = this._section.querySelector('[data-staffel-compare]');
      this._saleCompareEl = this._section.querySelector('[data-sale-compare]');
      this._suffixEl = this._section.querySelector('[data-staffel-suffix]');
      this._ctaTextEl = this._section.querySelector('.main-product__buy-button .main-product__buy-text');
      this._stickyPriceEl = this._section.querySelector('[data-sticky-price]');
    }

    /** The data set for the active variant, falling back to the only one present. */
    _variantData() {
      const variants = this._data.variants || {};
      if (this._variantId && variants[this._variantId]) return variants[this._variantId];
      const firstKey = Object.keys(variants)[0];
      return firstKey ? variants[firstKey] : null;
    }

    _quantity() {
      const value = parseInt(this._quantityInput?.value ?? '', 10);
      return Number.isFinite(value) && value > 0 ? value : 0;
    }

    /**
     * Write a quantity into the form. Setting the value and dispatching `blur` hands
     * it to quantity-selector-component, which validates min/step/max and emits
     * `quantity-selector:update` — so the sticky bar follows and all validation stays
     * in one place. This is the same route the sticky bar's own input takes.
     */
    _pick(quantity) {
      if (!this._quantityInput || !Number.isFinite(quantity)) return;
      this._quantityInput.value = String(quantity);
      this._quantityInput.dispatchEvent(new Event('blur', { bubbles: true }));
      this._render();
    }

    /** Show the price spans belonging to the active variant, hide the rest. */
    _syncCardPrices() {
      if (this.dataset.variantScoped !== 'true') return;
      this.querySelectorAll('[data-staffel-variant]').forEach((span) => {
        if (span.dataset.staffelVariant === String(this._variantId)) span.removeAttribute('hidden');
        else span.setAttribute('hidden', '');
      });
    }

    /** The highest level whose quantity the current quantity still reaches. */
    _activeLevel(levels, quantity) {
      let active = null;
      for (const level of levels) {
        if (level.qty > quantity) continue;
        if (!active || level.qty > active.qty) active = level;
      }
      return active;
    }

    _render() {
      const variantData = this._variantData();
      if (!variantData) return;
      const levels = variantData.levels || [];
      const quantity = this._quantity();
      const active = this._activeLevel(levels, quantity);
      const discounted = !!active && active.pct > 0;

      this._cards.forEach((card) => {
        const isActive = !!active && Number(card.dataset.quantity) === active.qty;
        card.setAttribute('aria-checked', isActive ? 'true' : 'false');
        card.classList.toggle('main-product__staffel-card--selected', isActive);
        card.tabIndex = isActive ? 0 : -1;
      });
      // Without a selected card the group still needs one tab stop.
      if (!active && this._cards.length) this._cards[0].tabIndex = 0;

      // innerHTML, not textContent: the shop's money format may contain markup, and
      // these strings come from the server-rendered `money-ex-vat` output. The sticky
      // bar already mirrors prices the same way.
      const price = discounted ? active.price : variantData.basePrice;
      if (this._priceEl && price) this._priceEl.innerHTML = price;
      if (this._stickyPriceEl && price) {
        this._stickyPriceEl.innerHTML =
          price + '<span class="main-product__sticky-price-suffix"> /st.</span>';
      }

      if (this._compareEl) {
        if (discounted) this._compareEl.removeAttribute('hidden');
        else this._compareEl.setAttribute('hidden', '');
      }

      // Actieprijs en staffelvergelijking sluiten elkaar uit: zonder staffelkorting is
      // de compare-at de juiste doorgestreepte prijs, met staffelkorting is dat de
      // prijs per fles zonder staffel.
      if (this._saleCompareEl) {
        if (discounted) this._saleCompareEl.setAttribute('hidden', '');
        else this._saleCompareEl.removeAttribute('hidden');
      }

      if (this._suffixEl) {
        const prefix = this._data.suffixPrefix || 'per';
        const unit = this._data.unitOne || '';
        this._suffixEl.textContent = discounted
          ? `${prefix} ${unit} · ${active.pctLabel}% ${this._data.discountWord || ''}`.trim()
          : `${prefix} ${unit}`.trim();
      }

      // A disabled button says "Uitverkocht"; a quantity label would contradict it.
      const ctaDisabled = this._ctaTextEl?.closest('button')?.hasAttribute('disabled');
      if (this._ctaTextEl && this._data.ctaTemplate && quantity > 0 && !ctaDisabled) {
        const unit = quantity === 1 ? this._data.unitOne : this._data.unitMany;
        this._ctaTextEl.textContent = this._data.ctaTemplate
          .replace('[aantal]', String(quantity))
          .replace('[eenheid]', unit || '');
      }
    }

    /** Arrow-key navigation, as a radiogroup is expected to behave. */
    _onGridKeydown(event) {
      const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
      if (!keys.includes(event.key)) return;
      const current = this._cards.indexOf(document.activeElement);
      if (current === -1) return;
      event.preventDefault();
      const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
      const next = (current + (forward ? 1 : -1) + this._cards.length) % this._cards.length;
      const card = this._cards[next];
      card.focus();
      this._pick(Number(card.dataset.quantity));
    }
  }

  customElements.define('staffel-tiers', StaffelTiers);
})();
